import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, KeyRound, X, Users } from "lucide-react";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";

const EMPTY = {
  id: "", code: "", name: "", email: "", gstin: "", pan: "", phone: "",
  contact_person: "", address: "", city: "", state: "", pincode: "",
  portal_enabled: true, password: "",
};

export function AdminCustomers({ token, onUnauthorized }) {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pwCustomer, setPwCustomer] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/admin/customers`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.status === 401) return onUnauthorized?.();
    setRows(await r.json());
  }, [token, onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const method = editing.id ? "PUT" : "POST";
      const url = editing.id ? `${API_BASE}/api/admin/customers/${editing.id}` : `${API_BASE}/api/admin/customers`;
      const body = { ...editing };
      if (!body.password) delete body.password;
      delete body.id;
      const r = await fetch(url, {
        method, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Save failed");
      setEditing(null); load();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!confirm(`Delete customer ${c.name}? This will also remove their uploaded documents.`)) return;
    const r = await fetch(`${API_BASE}/api/admin/customers/${c.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return alert("Delete failed");
    load();
  };

  const setPassword = async () => {
    if (pwValue.length < 8) return alert("Password must be at least 8 characters.");
    const r = await fetch(`${API_BASE}/api/admin/customers/${pwCustomer.id}/set-password`, {
      method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ password: pwValue }),
    });
    if (!r.ok) return alert((await r.json().catch(() => ({}))).detail || "Failed");
    alert("Password updated. Share it with the customer.");
    setPwCustomer(null); setPwValue("");
  };

  const filtered = (rows || []).filter((c) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return [c.name, c.email, c.code, c.gstin, c.city].some((v) => (v || "").toLowerCase().includes(t));
  });

  return (
    <div className="space-y-4" data-testid="admin-customers">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-[#0a2a5e] flex items-center gap-2"><Users className="h-5 w-5" /> Customers</h2>
          <p className="text-xs text-slate-500">Manage customer master and portal access.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="text" placeholder="Search customers…" value={q} onChange={(e) => setQ(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white" data-testid="add-customer-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Customer
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Code</th>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">City</th>
              <th className="px-4 py-3 font-semibold">GSTIN</th>
              <th className="px-4 py-3 font-semibold">Portal</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!rows && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {rows && filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No customers yet. Click "Add Customer".</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-4 py-3 font-mono text-xs">{c.code || "—"}</td>
                <td className="px-4 py-3 font-semibold text-[#0a2a5e]">{c.name}</td>
                <td className="px-4 py-3">{c.email}</td>
                <td className="px-4 py-3 text-slate-600">{c.city}</td>
                <td className="px-4 py-3 font-mono text-xs">{c.gstin || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.portal_enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {c.portal_enabled ? "Enabled" : "Disabled"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setPwCustomer(c)} className="text-slate-500 hover:text-[#0a2a5e] p-1.5" title="Set password"><KeyRound className="h-4 w-4" /></button>
                  <button onClick={() => setEditing({ ...c })} className="text-sky-600 hover:text-sky-800 p-1.5" title="Edit"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(c)} className="text-red-500 hover:text-red-700 p-1.5" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-[#0a2a5e]">{editing.id ? "Edit Customer" : "Add Customer"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700 p-1"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["code", "Customer Code", "text", false],
                ["name", "Customer Name *", "text", true],
                ["email", "Email *", "email", true],
                ["contact_person", "Contact Person", "text", false],
                ["phone", "Phone", "tel", false],
                ["gstin", "GSTIN", "text", false],
                ["pan", "PAN", "text", false],
                ["address", "Address", "text", false],
                ["city", "City", "text", false],
                ["state", "State", "text", false],
                ["pincode", "Pincode", "text", false],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                  <input type={type} value={editing[k] || ""} required={required}
                    onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                </div>
              ))}
              {!editing.id && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Portal Password (min 8 chars, optional now — can set later)</label>
                  <input type="text" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Leave blank to set later" />
                </div>
              )}
              <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={editing.portal_enabled} onChange={(e) => setEditing({ ...editing, portal_enabled: e.target.checked })}
                  className="h-4 w-4 accent-[#0a2a5e]" />
                Portal access enabled
              </label>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50 sticky bottom-0">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white" data-testid="save-customer-btn">
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {pwCustomer && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPwCustomer(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-[#0a2a5e] mb-1">Set Password</h3>
            <p className="text-sm text-slate-500 mb-4">Set a portal password for <b>{pwCustomer.name}</b>. They can change it after login.</p>
            <input type="text" value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="Min 8 characters"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mb-4" autoFocus />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPwCustomer(null)} className="rounded-full">Cancel</Button>
              <Button onClick={setPassword} className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white">Save Password</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
